import { TableInfo, useTable } from 'api';
import Button from 'components/button/Button';
import { DateRangePicker } from 'components/dateRangePicker/DateRangePicker';
import { CryptoHistoryItem, HistoryTable, ID_COLUMN } from 'components/historyTable/HistoryTable';
import { canShowLiquidateModal } from 'components/modals/LiquidateCryptoModal';
import Pagination from 'components/pagination/Pagination';
import { SubPageLoader } from 'components/subPageLoader/SubPageLoader';
import { endpoints } from 'endpoints.config';
import { SearchBox } from 'helpers/searchBox/SearchBox';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';
import { selectCultureCode } from 'reducers/language';
import { ModalTypes, openModal, selectModalState } from 'reducers/modal';
import { useWindowDimensions } from 'helpers/useWindowDimensions';
import { TableEntryInfoPanel } from '../../components/TableEntryInfoPanel/TableEntryInfoPanel';
import styles from '../../components/TableEntryInfoPanel/TableEntryInfoPanel.module.scss';
import { selectRefreshTransactionFlag } from '../../reducers/utility';
import { useAfterMountEffect } from '../../helpers/useAfterMountEffect';
import moment from 'moment';

type Props = {
    ticker: string | undefined;
};

export const checkIsPendingCrypto = (row?: CryptoHistoryItem) =>
    row?.transactionAmounts__ApprovalStatus !== 'Approval' &&
    !!(
        row?.customerAssetAccountsTransactions__bPending ||
        row?.customerAssetAccountsTransactions__bReserved ||
        row?.customerAssetAccountsTransactions__bFrozen
    );

export const CryptoAccountsHistory = ({ ticker }: Props) => {
    const { t } = useTranslation('crypto');
    const refreshTransactionsFlag = useSelector(selectRefreshTransactionFlag);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const table = useTable<CryptoHistoryItem, Error>({
        url: endpoints.cryptosmodule.historyList,
        bClearFilters: false,
        bClearSearch: true,
        multiSort: false,
        filters: `assets__Code EQ (${ticker?.toUpperCase()})`,
        params: { assetCode: ticker?.toUpperCase() },
    });
    const modalState = useSelector(selectModalState);
    const { customerMenuLinks } = useSelector(selectComponentResources);
    const cultureCode = useSelector(selectCultureCode);
    const dispatch = useDispatch();
    const { width } = useWindowDimensions();

    useAfterMountEffect(() => {
        table.reload();
    }, [refreshTransactionsFlag]);

    const handleOpenWithdrawModal = () => {
        if (!ticker) return null;
        if (ticker.toLowerCase() === 'dusd') {
            dispatch(
                openModal({
                    modalType: ModalTypes.WITHDRAW_CRYPTO_AS_FIAT,
                })
            );
        } else {
            dispatch(
                openModal({
                    modalType: ModalTypes.WITHDRAW_CRYPTO,
                    data: {
                        crypto: ticker.toUpperCase(),
                        withdrawUrl: endpoints.cryptosmodule.withdraw,
                    },
                })
            );
        }
    };

    const openLiquidateModal = () => {
        if (!ticker) return;
        dispatch(
            openModal({
                modalType: ModalTypes.LIQUIDATE_CRYPTO,
                data: { crypto: ticker.toUpperCase(), fromAccount: 1000 },
            })
        );
    };

    const openDepositModal = () => {
        if (!ticker) return;
        dispatch(
            openModal({
                modalType: ModalTypes.DEPOSIT_CRYPTO,
                data: {
                    crypto: ticker.toUpperCase(),
                    depositEndpoint: endpoints.cryptosmodule.getDepositAddress,
                },
            })
        );
    };

    useEffect(() => {
        if (
            table.data &&
            !table.data.details.filters.some(
                (filter: any) =>
                    filter.columnKey === 'assets__Code' &&
                    filter.filters.some(
                        (filterObj: { value: string }) =>
                            filterObj.value === (ticker?.toUpperCase() ?? '')
                    )
            )
            //table.data.details.filters !== `assets__Code EQ (${ticker?.toUpperCase()})`
        ) {
            table.data.actions.changeFilter('assets__Code', [
                { columnKey: 'assets__Code', operator: 'EQ', value: ticker?.toUpperCase() ?? '' },
            ]);
        }
    });

    useEffect(() => {
        if (isPageLoading && !table.loading) {
            setIsPageLoading(false);
        }
    }, [setIsPageLoading, isPageLoading, table.data, table.loading]);

    const [selectedRow, setSelectedRow] = useState<number>();
    const onRowClick = (id: number) => {
        if (selectedRow === id) setSelectedRow(undefined);
        else setSelectedRow(id);
    };
    useEffect(() => {
        if (width >= 1400 || !selectedRow) return;
        else
            dispatch(
                openModal({
                    modalType: ModalTypes.TRANSACTION_DETAILS,
                    data: {
                        rowData: {
                            id: selectedData!.customerAssetAccountsTransactions__Id,
                            amount: selectedData!.customerAssetAccountsTransactions__Amount,
                            moneyIn: selectedData!.transactionTypes__Type === 'Buy',
                            reference: `${selectedData!.assets__Code}: ${
                                selectedData!
                                    .customerAssetAccountsTransactions__CustomerAssetAccountsTransactionsCode
                            }`,
                            currencyCode: selectedData!.assets__Code,
                            isPending: checkIsPendingCrypto(selectedData),
                            approvalStatus: selectedData!.transactionAmounts__ApprovalStatus,
                            bEligibleForReverse:
                                selectedData!.transactionAmounts__bEligibleForReverse,
                            bCancellable: selectedData!.bCancellable,
                        },
                        currencySymbol: '',
                    },
                })
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRow, width]);
    useEffect(() => {
        if (modalState.modalType) return;
        else setSelectedRow(undefined);
    }, [modalState]);
    const selectedData = table.data?.details.listData.find(
        (entry) => entry[ID_COLUMN as keyof CryptoHistoryItem] === selectedRow
    );
    const emptyTable = table?.data?.details.listData.length === 0;

    const setFilter = (startDate: Date, endDate: Date) => {
        table.data?.actions.changeFilter('customerAssetAccountsTransactions__Date', [
            {
                columnKey: 'customerAssetAccountsTransactions__Date',
                operator: 'BETWEEN',
                value: `${moment(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss')}TO${moment(
                    endDate
                )
                    .endOf('day')
                    .format('YYYY-MM-DDTHH:mm:ss')}`,
            },
        ]);
    };
    if (isPageLoading)
        return (
            <SubPageLoader
                message={`Loading your ${ticker ? ticker.toUpperCase() : 'crypto'} account`}
            />
        );
    return (
        <div className="Cryptos">
            <div className="Portfolio">
                <div className="TableHeader">
                    <DateRangePicker setFilter={setFilter} cultureCode={cultureCode} />
                    <SearchBox
                        key={'searchBox'}
                        placeholder="Search transactions"
                        initialSearchString={table?.data?.details.searchString ?? ''}
                        changeSearch={table?.data?.actions.changeSearch}
                        goToPage={table?.data?.actions.goToPage}
                    />
                    <div className="HeaderButtons">
                        <Button priority="primary" className="notranslate" translate="no" onClick={handleOpenWithdrawModal}>
                            {t('withdraw')}{' '}
                            {ticker?.toLowerCase() === 'dusd' ? 'USD' : ticker?.toUpperCase()}
                        </Button>
                        {ticker && ticker.toLowerCase() !== 'dusd' && (
                            <Button priority="primary" color="second" className="notranslate" translate="no" onClick={openDepositModal}>
                                {t('deposit')} {ticker?.toUpperCase()}
                            </Button>
                        )}
                        {ticker && canShowLiquidateModal(ticker, customerMenuLinks) && (
                            <Button priority="secondary" className="notranslate" translate="no" onClick={openLiquidateModal}>
                                {t('liquidate')} {ticker?.toUpperCase()}
                            </Button>
                        )}
                    </div>
                </div>
                <div className="TableWrapper">
                    <HistoryTable
                        table={table as TableInfo}
                        selectedIds={selectedRow ? [selectedRow] : undefined}
                        onRowClick={onRowClick}
                        noRowButton
                        idColumn={''}
                    />
                    {!emptyTable && (
                        <div className={styles.TableEntryInfoPanelWrapper}>
                            <TableEntryInfoPanel
                                selectedRowData={
                                    selectedData
                                        ? {
                                              id: selectedData.customerAssetAccountsTransactions__Id,
                                              amount: selectedData.customerAssetAccountsTransactions__Amount,
                                              moneyIn:
                                                  selectedData.transactionTypes__Type === 'Buy',
                                              reference:
                                                  selectedData.customerAssetAccountsTransactions__CustomerAssetAccountsTransactionsCode,
                                              currencyCode: selectedData.assets__Code,
                                              isPending: checkIsPendingCrypto(selectedData),
                                              approvalStatus:
                                                  selectedData.transactionAmounts__ApprovalStatus,
                                              bEligibleForReverse:
                                                  selectedData.transactionAmounts__bEligibleForReverse,
                                              bCancellable: selectedData.bCancellable,
                                          }
                                        : undefined
                                }
                                closePanel={() => setSelectedRow(undefined)}
                            />
                        </div>
                    )}
                </div>
                <Pagination table={table} />
            </div>
        </div>
    );
};
